import { Box, CircularProgress } from "@mui/material";
import { DataGridPro, GridFilterItem, GridFilterModel, useGridApiRef } from "@mui/x-data-grid-pro";
import { Ico_Filter_Component, Ico_MenuDots, Ico_SortAscending, Ico_SortDescending } from "@sagecentral/core-ui";
import { GridBox, GridNoRowsOverlay, GridToolbar } from "components/shared/grids";
import { IWorkItemWorkflow } from "models/work-items";
import useWorkflowTasksColumns from "./useWorkflowTasksColumns";
import { customLocaleText } from 'models/grids/HeaderFilterLocaleText';
import { useCallback, useEffect, useRef, useState } from "react";

type WorkflowTasksProps = {
    workflow: IWorkItemWorkflow | null;
    project?: boolean;
    refresh?: () => void;
    "aria-label"?: string;
    tabIndex?: number;
};

const NoRowsOverlay = () => {
    return (
        <Box role="status" aria-live="polite">
            <GridNoRowsOverlay text={`No Tasks found.`} />
        </Box>
    );
};

const WorkflowTasksGrid = (props: WorkflowTasksProps) => {
    const { 
        workflow,
        refresh
    } = props;
    
    const gridState = useRef({
        scrollTop: 0,
        selectedRows: new Set<number>(),
        isFirstRender: true
    });

const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: []
});

    const apiRef = useGridApiRef();
    
    const handleRefresh = useCallback(() => {
        if (!apiRef.current) return;
        
        gridState.current = {
            scrollTop: apiRef.current.getScrollPosition().top,
            selectedRows: new Set(
                apiRef.current.getSelectedRows() ? 
                Array.from(apiRef.current.getSelectedRows().keys()).map(id => Number(id)) : 
                []
            ),
            isFirstRender: false
        };
        
        // Call the original refresh function
        if (refresh) {
            refresh();
        }
    }, [refresh, apiRef]);

    const columns = useWorkflowTasksColumns({ onRefresh: handleRefresh });
    
    useEffect(() => {
        if (!apiRef.current || !workflow?.tasks || gridState.current.isFirstRender) return;
        
        const timeoutId = setTimeout(() => {
            if (gridState.current.scrollTop > 0) {
                apiRef.current.scrollToIndexes({
                    rowIndex: Math.floor(gridState.current.scrollTop / 52) // Approximate row height
                });
            }
            
            if (gridState.current.selectedRows.size > 0) {
                const rowIds = Array.from(gridState.current.selectedRows);
                apiRef.current.setRowSelectionModel(rowIds);
            }
        }, 100);
        
        return () => clearTimeout(timeoutId);
    }, [workflow, apiRef]);

    const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
        const processedItems = newFilterModel.items.map((item: GridFilterItem) => ({
            ...item,
            operator: item.operator || 'contains',
        }));
    
        const isAddingNewFilter = newFilterModel.items.length > filterModel.items.length;
        
        const itemsWithChanges = processedItems.map(item => {
            const previousItem = filterModel.items.find(
                prevItem => prevItem.id === item.id
            );
            return {
                ...item,
                isFieldChange: previousItem && previousItem.field !== item.field,
                isOperatorChange: previousItem && previousItem.operator !== item.operator
            };
        });
    
        if (isAddingNewFilter) {
            setFilterModel({
                ...newFilterModel,
                items: processedItems,
            });
        } else {
            const cleanedItems = itemsWithChanges.filter(item => 
                (item.value !== '' && item.value != null) || 
                item.isFieldChange ||
                item.isOperatorChange  // Preserve items when operator changes
            );
            
            setFilterModel({
                ...newFilterModel,
                items: cleanedItems.map(({ isFieldChange, isOperatorChange, ...item }) => item)
            });
        }
    };

    if (workflow === null) {
        return null;
    }

    return (
        <GridBox getData={() => workflow.tasks ?? []}>
            <Box 
                sx={{ maxHeight: "65vh", display: 'flex' }}
                role="region"
                aria-label={props["aria-label"] || "Workflow tasks"}
            >
                <DataGridPro
                    sx={{ height: 'auto' }}
                    apiRef={apiRef}
                    rows={workflow.tasks ?? []}
                    columns={columns}
                    getRowId={(r: any) => r.id}
                    filterMode="client"
                    sortingMode="client"
                    paginationMode='client'
                    filterModel={filterModel}
                    onFilterModelChange={handleFilterModelChange}
                    pagination={true}
                    pageSizeOptions={[25, 50, 75, 100]}                    
                    slotProps={{
                        row: {
                            role: 'row',
                            tabIndex: -1
                        },
                        cell: {
                            tabIndex: -1
                        },
                        root: {
                            'aria-label': props["aria-label"] || 'Workflow tasks grid',
                            'tabIndex': props.tabIndex ?? 0
                        }
                    }}
                    slots={{
                        loadingOverlay: () => (<CircularProgress aria-label="Loading tasks" />),
                        columnMenuSortAscendingIcon: Ico_SortAscending,
                        columnMenuSortDescendingIcon: Ico_SortDescending,
                        columnSortedAscendingIcon: Ico_SortAscending,
                        columnSortedDescendingIcon: Ico_SortDescending,
                        columnUnsortedIcon: undefined,
                        moreActionsIcon: Ico_MenuDots,
                        quickFilterIcon: Ico_Filter_Component,
                        columnFilteredIcon: Ico_Filter_Component,
                        quickFilterClearIcon: Ico_Filter_Component,
                        columnMenuFilterIcon: Ico_Filter_Component,
                        noRowsOverlay: () => NoRowsOverlay(),
                        toolbar: GridToolbar
                    }}
                    headerFilters
                    localeText={customLocaleText}
                    loading={!workflow}
                    
                    getRowClassName={(params: any) =>
                        params.indexRelativeToCurrentPage % 2 === 0 ? 'row even' : 'row odd'
                    }
                    pinnedColumns={{ left: ['name'] }}
                />
            </Box>
        </GridBox>
    );
};

export default WorkflowTasksGrid;